import { createReducer, isAnyOf } from '@reduxjs/toolkit';
import { EVENT_WIZARD_STEP_CONFIGS } from 'pages/EventWizard/stepConfigs';
import { eventApi } from 'store/apis/event/eventApi';

import { eventWizardActions } from './eventWizard.actions';
import { createEvent } from './thunks';
import { EventWizardState } from './types';
import { getEventWizardInitialSteps } from './utils';

export const initialState: EventWizardState = {
  isLoading: false,
  form: {
    mode: undefined,
    isDirty: false,
    requiredFieldsOnceFilled: false,
  },
  progressTracker: {
    steps: getEventWizardInitialSteps('create', EVENT_WIZARD_STEP_CONFIGS),
    activeStep: 0,
  },
};

export const eventWizardReducer = createReducer(initialState, (builder) => {
  builder
    .addCase(eventWizardActions.initialized, (_, { payload: { id, isRepeat, isWithTemplate } }) => {
      const mode = isRepeat ? 'repeat' : id ? 'edit' : isWithTemplate ? 'template' : 'create';
      return {
        ...initialState,
        eventId: id,
        form: {
          ...initialState.form,
          mode,
        },
        progressTracker: {
          steps: getEventWizardInitialSteps(mode, EVENT_WIZARD_STEP_CONFIGS),
          activeStep: 0,
        },
      };
    })
    .addCase(eventWizardActions.progressTracker.navigateToStep, (state, { payload: newStep }) => {
      state.progressTracker.activeStep = newStep;
      const stepObj = state.progressTracker.steps[newStep];
      if (stepObj) stepObj.status = 'IN_PROGRESS';
    })
    .addCase(eventWizardActions.progressTracker.stepValidationCompleted, (state, { payload: { step, isValid } }) => {
      const stepObj = state.progressTracker.steps[step];
      if (stepObj) stepObj.status = isValid ? 'COMPLETED' : 'INCOMPLETE';
    })
    .addCase(eventWizardActions.form.completed, (state) => {
      state.form.requiredFieldsOnceFilled = true;
    })
    .addCase(createEvent.fulfilled, () => ({
      ...initialState,
    }))
    .addMatcher(
      isAnyOf(
        eventWizardActions.details.videoDropzone.uploadCanceled,
        eventWizardActions.details.videoDropzone.uploadCompleted,
        eventWizardActions.details.videoDropzone.uploadFailed,
        eventWizardActions.publishButton.clicked.rejected,
        eventWizardActions.updateButton.clicked.rejected,
        eventApi.endpoints.postEventImage.matchRejected,
        eventApi.endpoints.createEventQuestions.matchRejected
      ),
      (state) => {
        state.progressTracker.steps = state.progressTracker.steps.map((step) => ({
          ...step,
          isDisabled: false,
        }));
        const activeStepObj = state.progressTracker.steps[state.progressTracker.activeStep];
        if (activeStepObj) activeStepObj.isLoading = false;
        state.isLoading = false;
      }
    )
    .addMatcher(
      isAnyOf(
        eventWizardActions.publishButton.clicked.pending,
        eventWizardActions.details.videoDropzone.fileSelected,
        eventWizardActions.updateButton.clicked.pending,
        eventApi.endpoints.postEventImage.matchPending,
        eventApi.endpoints.createEventQuestions.matchPending
      ),
      (state) => {
        state.progressTracker.steps = state.progressTracker.steps.map((step) => ({
          ...step,
          isDisabled: true,
        }));
        const activeStepObj = state.progressTracker.steps[state.progressTracker.activeStep];
        if (activeStepObj) activeStepObj.isLoading = true;
        state.isLoading = true;
      }
    )
    .addMatcher(
      isAnyOf(
        eventWizardActions.exited,
        eventWizardActions.updateButton.clicked.fulfilled,
        eventWizardActions.eventLimitReachedModal.confirmedButton.clicked
      ),
      (state) => ({ ...state, ...initialState })
    );
});
